import {authHeader} from '../helpers/auth-header';
import {config} from '../helpers/config'
import {EXTERNAL_USER_ROLES} from '../store/AppConstants'
import axios from './axios';
import axiosFileDownloader from './axiosFileDownloader';
import axiosFileUploader from './axiosFileUploader';

export function handleResponse(response) {
    return new Promise((resolve, reject) => {
        let contentType = response.headers.get("content-type");
        if (response.ok) {
            // return json if it was returned in the response

            if (contentType && contentType.includes("application/json")) {
                response.json().then(json => resolve(json));
            } else {
                resolve("success");
            }
        } else {

            if (contentType && contentType.includes("application/json")) {
                // return error message from response body
                response.json().then(json => reject(json));
            } else {
                response.text().then(text => reject(text));
            }
        }
    });
}

export function handleError(error) {
    return Promise.reject(error && error.message);
}

export default class UserService {

    logout() {
        // remove user from local storage to log user out
        localStorage.removeItem('user');
    }

    async checkLogin() {
        let user = JSON.parse(localStorage.getItem('user'));
        if (!user || !user.token) return false;
        const requestOptions = {
            method: 'GET',
            headers: authHeader()
        };
        return await fetch(config.apiUrl + 'api/user/', requestOptions);
    }

    static isLoggedIn() {
        let user = JSON.parse(localStorage.getItem('user'));
        if (user && user.token) {
            return true;
        }
        return false;
    }

    isCustomerUser(user) {
        if (user && user.menu && (EXTERNAL_USER_ROLES.includes(user.menu.role))) {
            return true;
        }
        return false;
    }

    login(username, password) {
        const requestOptions = {
            method: 'POST',
            headers: authHeader(),
            body: JSON.stringify({username, password})
        };

        return fetch(config.apiUrl + 'api/user/authenticate', requestOptions)
            .then(handleResponse, handleError)
            .then(user => {
                // login successful if there's a jwt token in the response
                if (user && user.token) {
                    // store user details and jwt token in local storage to keep user logged in between page refreshes
                    localStorage.setItem('user', JSON.stringify(user));
                }
                return user;
            });
    }

    resetPassword(username) {
        const requestOptions = {
            method: 'POST',
            headers: authHeader(),
            body: JSON.stringify({username})
        };

        return fetch(config.apiUrl + 'api/user/reset', requestOptions).then(handleResponse, handleError)
    }

    updatePasswordByReset(password, repassword, token) {
        const requestOptions = {
            method: 'POST',
            headers: authHeader(),
            body: JSON.stringify({password, repassword, token})
        };
        return fetch(config.apiUrl + 'api/user/updatePassword', requestOptions)
            .then(handleResponse, handleError)
            .then(user => {
                // password updated successfully, if there's a jwt token in the response
                if (user && user.token) {
                    // store user details and jwt token in local storage to keep user logged in between page refreshes
                    localStorage.setItem('user', JSON.stringify(user));
                }

                return user;
            });
    }

    updatePasswordByAdmin(password, repassword, email) {
        let requestBody = {
            password: password,
            repassword: repassword,
            token: email
        };
        return axios.put('api/admin/user/update/password', requestBody);
    }


    listUsers(req, type) {
        return axios.post('api/admin/user/list?Type=' + type, req);
    }

    async getUser() {
        return JSON.parse(localStorage.getItem('user'));
    }

    getLoggedInUser() {
        return JSON.parse(localStorage.getItem('user'));
    }

    getLoggedInUserAccountID() {
        let currentUser = this.getLoggedInUser();
        return currentUser.menu.accountID;
    }

    getCurrentUserPrimaryRole(currentUser) {
        if (!currentUser) {
            currentUser = JSON.parse(localStorage.getItem('user'));
        }
        return currentUser.roles.find(r => r.isPrimary);
    }

    getLoggedInUserFromDb(isNewTokenRequired) {
        let url = 'api/user/self';
        if (isNewTokenRequired) {
            url = 'api/user/self/auth'
        }
        return axios.get(url)
    }

    refreshLocalStorage() {
        this.getLoggedInUserFromDb(true).then(userResponse => {
            let user = userResponse.data;
            localStorage.clear();
            if (user) {
                localStorage.setItem('user', JSON.stringify(user));
                window.location.reload();
            }
        }, error => {
            console.log(error);
        });
    }

    hasPrivilege(user, privilege) {
        if (user && user.menu && user.menu.privileges) {
            return (user.menu.privileges || []).includes(privilege);
        }
        return false;
    }

    hasRole(user, role) {
        if (user && user.menu && (user.menu.role === role)) {
            return true;
        }
        return false;
    }

    saveUser(user) {
        if (user.id) {
            return axios.put('api/admin/user/', user)
        } else {
            return axios.post('api/admin/user/', user)
        }
    }

    listRoles() {
        const requestOptions = {
            method: 'GET',
            headers: authHeader(),
        };

        return fetch(config.apiUrl + 'api/admin/role/list', requestOptions).then(handleResponse, handleError);
    }

    listUserRoles(id) {

        let url = 'api/admin/user/' + id + '/role';
        return axios.get(url)
    }

    addRole(id, newrole) {
        newrole.userId = id;
        const requestOptions = {
            method: 'POST',
            headers: authHeader(),
            body: JSON.stringify(newrole),
        };
        return fetch(config.apiUrl + 'api/admin/user/addrole', requestOptions).then(handleResponse, handleError);
    }

    setPrimaryRole(userRoleId) {
        let url = 'api/admin/user/role/primary?userRoleId=' + userRoleId;
        return axios.post(url);
    }

    removeRole(id) {
        const requestOptions = {
            method: 'DELETE',
            headers: authHeader(),
        };
        return fetch(config.apiUrl + 'api/admin/user/removerole/' + id, requestOptions).then(handleResponse, handleError);
    }

    getUsersStat() {
        const requestOptions = {
            method: 'GET',
            headers: authHeader(),
        };

        return fetch(config.apiUrl + 'api/dashboard/user/stats', requestOptions).then(handleResponse, handleError);
    }

    getSallyUser() {
        return axios.get('api/dashboard/sally/users');
    }

    deleteUser(id) {
        const requestOptions = {
            method: 'PUT',
            headers: authHeader(),
        };

        return fetch(config.apiUrl + 'api/admin/user/deleteuser/' + id, requestOptions).then(handleResponse, handleError);
    }

    checkUsernameAvailibility(username) {
        let url = 'api/user/check/username/available';
        return axios.post(url, {username})
    }

    searchUser(query, role) {
        let url = "";
        switch (role) {
            case "sales":
                url = 'api/admin/user/sales/search';
                break;
            default:
                url = 'api/admin/user/search';
                break;
        }

        return axios.post(url, {query: query});
    }

    findUsers(ids, usernames, emails) {
        let request = {
            ids: ids,
            usernames: usernames,
            emails: emails
        };
        return axios.post('api/admin/user/list/search/in', request);
    }

    findAlreadyInvited(emails) {
        return axios.post('api/customer/invite/list', emails);
    }

    generateUserSummaryInExcelReport(req) {
        return axiosFileDownloader.post('api/admin/user/GenerateUserSummaryInExcel', req);
    }

    getUserCategories() {
        return axios.get('api/admin/user/categories');
    }

    getUserDetail() {
        return axios.get("api/user/self/detail");
    }

    getUserListByAccountID(accountID) {
        return axios.get('api/admin/user/list/by/accountID?accountID=' + accountID);
    }

    getUserDetailByUserID(userID) {
        return axios.get('api/admin/debtor/team/user/detail?userID=' + userID);
    }

    saveDebtorTeamUser(request) {
        return axios.post('api/admin/user/manage/debtor/team', request);
    }

    generateExportUser() {
        return axiosFileDownloader.get('api/admin/user/GenerateUsersImportFile');
    }

    importUser(request) {
        return axiosFileUploader.post('api/admin/user/import', request);
    }
    getAccountStoreCode(accountID) {
        return axios.get("api/customer/account/storecode?accountID=" + accountID);
    }

    generateUsername() {
        return axios.get('api/admin/user/username/generate');
    }

    lookupSwishUsername(email) {
        return axios.get('api/admin/user/swish/lookup?email=' + encodeURIComponent(email));
    }

    validateSwishUsername(username) {
        return axios.get('api/admin/user/swish/validate?username=' + encodeURIComponent(username));
    }
}